import {
  CURRENCY_FORMAT_OVERRIDES,
  DEFAULT_CURRENCY,
  DEFAULT_CURRENCY_FORMAT,
  formatPrice as formatPriceFeCommon,
  toNumber,
  toBCP47Locale,
} from '@lastbrand/fe-common'
import { useCallback, useMemo } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { CONFIG_DISABLE_LEGACY_CURRENCY_FORMATTING } from '@src/config/env'
import type { TAbsoluteLocale } from '@src/config/localisation/locale-config.types'

import type {
  CurrencyCode,
  CurrencyFormatName,
  FormattedPriceResult,
  PriceInput,
  UseFormattedPriceOptions,
} from '@lastbrand/fe-common'

/**
 * Get currency code from locale context and region details
 */
export function getCurrencyFromContext(
  locale: string,
  regionDetails: { regions?: Record<string, { defaultCurrencyIsoCode?: string }> } | undefined,
): CurrencyCode {
  const country = locale.split('-')[1]?.toLowerCase()
  const fromRegionDetails = country
    ? (regionDetails?.regions?.[country]?.defaultCurrencyIsoCode as CurrencyCode | undefined)
    : undefined
  return fromRegionDetails ?? DEFAULT_CURRENCY
}

/**
 * Format a price using Intl.NumberFormat or custom override (via fe-common).
 * Standalone function for use outside React components.
 * Passes ecomm-ssr config for legacy currency formatting.
 *
 * @param amount - The numeric amount to format
 * @param formatName - The semantic format name
 * @param locale - The locale to use for formatting (internal format like 'en-us')
 * @param currency - The currency code
 * @param options - Additional formatting options
 * @returns The formatted price string
 */
export function formatPrice(
  amount: PriceInput,
  formatName: CurrencyFormatName,
  locale: TAbsoluteLocale,
  currency: CurrencyCode,
  options: UseFormattedPriceOptions = {},
): string {
  return formatPriceFeCommon(amount, formatName, locale, currency, {
    ...options,
    overrides: options.overrides ?? CURRENCY_FORMAT_OVERRIDES,
    disableLegacyCurrencyFormatting:
      options.disableLegacyCurrencyFormatting ?? CONFIG_DISABLE_LEGACY_CURRENCY_FORMATTING,
  })
}

/**
 * Hook for formatting a price with locale and region awareness.
 *
 * @param amount - The numeric amount to format
 * @param formatName - The semantic format name (defaults to 'NARROW_SYMBOL')
 * @param options - Optional formatting options
 * @returns Object containing formatted price, raw value, and currency code
 *
 * @example
 * ```tsx
 * const { formatted } = useFormattedPrice(99.99)
 * // formatted: "$99.99" (USD) or "99,99 $" (CAD in fr-CA)
 *
 * // With currency code prefix (e.g. CAD $99.99)
 * const { formatted } = useFormattedPrice(99.99, 'SYMBOL_WITH_CODE')
 * ```
 */
export function useFormattedPrice(
  amount: PriceInput,
  formatName: CurrencyFormatName = DEFAULT_CURRENCY_FORMAT,
  options: UseFormattedPriceOptions = {},
): FormattedPriceResult {
  const { locale, regionDetails } = useLocaleContext()

  return useMemo(() => {
    const numericAmount = toNumber(amount)
    const currency: CurrencyCode =
      options.currency ?? getCurrencyFromContext(locale, regionDetails as Parameters<typeof getCurrencyFromContext>[1])

    // Return empty formatted string and null value for invalid amounts
    if (numericAmount == null || !Number.isFinite(numericAmount)) {
      return {
        formatted: '',
        value: null,
        currency,
      }
    }

    const formatted = formatPrice(numericAmount, formatName, locale, currency, options)

    return {
      formatted,
      value: numericAmount,
      currency,
    }
  }, [amount, formatName, locale, regionDetails, options])
}

/**
 * Hook that returns a formatter function for formatting prices.
 * Useful when you need to format multiple prices with the same settings.
 *
 * @param formatName - The semantic format name (defaults to 'NARROW_SYMBOL')
 * @param options - Optional formatting options
 * @returns A function that formats prices
 *
 * @example
 * ```tsx
 * const formatPrice = useFormatPrice()
 * return (
 *   <ul>
 *     {prices.map(price => <li key={price}>{formatPrice(price)}</li>)}
 *   </ul>
 * )
 * ```
 */
export function useFormatPrice(
  formatName: CurrencyFormatName = 'NARROW_SYMBOL',
  options: UseFormattedPriceOptions = {},
): (amount: PriceInput) => string {
  const { locale, regionDetails } = useLocaleContext()

  const currency: CurrencyCode = useMemo(() => {
    return (
      options.currency ?? getCurrencyFromContext(locale, regionDetails as Parameters<typeof getCurrencyFromContext>[1])
    )
  }, [locale, regionDetails, options.currency])

  return useCallback(
    (amount: PriceInput) => {
      return formatPrice(amount, formatName, locale, currency, options)
    },
    [formatName, locale, currency, options],
  )
}

/**
 * Returns a formatter that renders currency as `${symbol}${code}` (e.g. `$USD`, `C$CAD`)
 * based on the region's default locale + currency.
 *
 * @example
 * ```tsx
 * const getCurrencyDisplay = useCurrencyDisplay()
 * const display = getCurrencyDisplay('us') // Returns "$USD"
 * const cadDisplay = getCurrencyDisplay('ca') // Returns "$CAD"
 * ```
 */
export function useCurrencyDisplay(): (regionCode: string) => string {
  const { regionDetails } = useLocaleContext()

  return useCallback(
    (regionCode: string): string => {
      try {
        const regionDetailsAny = regionDetails as
          | {
              regions?: Record<
                string,
                {
                  defaultCurrencyIsoCode?: string
                  defaultLanguageIsoCode?: string
                }
              >
            }
          | undefined
        const region = regionDetailsAny?.regions?.[regionCode]
        if (!region) return ''

        const currencyCode = region.defaultCurrencyIsoCode
        const defaultLanguageIsoCode = region.defaultLanguageIsoCode
        if (!currencyCode || !defaultLanguageIsoCode) return ''

        const targetLocale = toBCP47Locale(defaultLanguageIsoCode, {
          disableLegacyCurrencyFormatting: CONFIG_DISABLE_LEGACY_CURRENCY_FORMATTING,
        })

        // Format 1 unit of currency to get the symbol
        const formatted = (1).toLocaleString(targetLocale, {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })

        // Remove all digits to get just the symbol
        const symbol = formatted.replace(/\d/g, '')
        return `${symbol}${currencyCode}`
      } catch {
        return ''
      }
    },
    [regionDetails],
  )
}
